import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  Box, Button, Card, CardContent, Checkbox, FormControlLabel,
  Grid, InputLabel, TextField, Typography, CircularProgress, Chip, Radio, RadioGroup,
} from "@mui/material";
import { ArrowBack, Search, CheckCircle, PhotoCamera } from "@mui/icons-material";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Axios from "../axios";
import DateInputField from "./DateInputField";

const getTodayDate = () => new Date().toISOString().split("T")[0];
const getMinEndDate = (startDate) => {
  if (!startDate) return getTodayDate();
  const d = new Date(startDate);
  d.setDate(d.getDate() + 1);
  return d.toISOString().split("T")[0];
};

const sanitizeRateToken = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const getRateSupplierName = (rate = {}) =>
  rate.supplierName ||
  rate.providerName ||
  rate.provider?.name ||
  rate.provider ||
  rate.supplier?.name ||
  rate.supplier ||
  "Unknown Supplier";
const buildRateOptionId = ({ planId, refundLabel, supplierName, rateId }) =>
  [planId || "plan", refundLabel || "refund", supplierName || "unknown-supplier", rateId || "rate"]
    .map((part) => sanitizeRateToken(part))
    .join("__");

const formFontFamily = '"Poppins", sans-serif';
const formSx = {
  fontFamily: formFontFamily,
  "& .form-title": {
    color: "#262626",
    fontFamily: `${formFontFamily} !important`,
    fontSize: "2rem",
    fontWeight: 600,
    lineHeight: 1.3,
  },
  "& .MuiTypography-root, & .MuiButton-root, & .MuiInputBase-root, & .MuiInputBase-input, & .MuiInputLabel-root, & .MuiMenuItem-root, & .MuiChip-root, & .MuiFormHelperText-root": {
    fontFamily: `${formFontFamily} !important`,
  },
  "& .MuiTypography-h6": {
    fontSize: "1.25rem",
    fontWeight: 600,
    lineHeight: 1.35,
  },
  "& .MuiTypography-subtitle1": {
    fontSize: "1.1rem",
    fontWeight: 600,
  },
  "& .MuiTypography-subtitle2": {
    fontSize: "1rem",
    fontWeight: 600,
  },
  "& .MuiTypography-body1": {
    fontSize: "1rem",
  },
  "& .MuiTypography-body2": {
    fontSize: "0.95rem",
  },
  "& .MuiTypography-caption": {
    fontSize: "0.85rem",
  },
  "& .MuiInputBase-input, & .MuiSelect-select": {
    fontSize: "1rem !important",
  },
  "& .MuiInputLabel-root": {
    fontSize: "1rem !important",
  },
  "& .MuiButton-root": {
    fontSize: "0.95rem !important",
    textTransform: "none",
  },
  "& .MuiChip-label": {
    fontSize: "0.85rem",
  },
};

const FeatureDealsForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND;

  const [loading, setLoading] = useState(false);
  const [fetchingHotel, setFetchingHotel] = useState(false);

  const fileInputRef = useRef(null);
  const startDateInputRef = useRef(null);
  const endDateInputRef = useRef(null);

  // Step 1 inputs
  const [hotelId, setHotelId] = useState("");
  const [numOfAdultsInput, setNumOfAdultsInput] = useState("2");
  const [numOfChildrenInput, setNumOfChildrenInput] = useState("0");
  const [dealStartDate, setDealStartDate] = useState("");
  const [dealEndDate, setDealEndDate] = useState("");
  const [savedStartDate, setSavedStartDate] = useState("");

  // Itinerary dates
  const [itineraryStartDate, setItineraryStartDate] = useState("");
  const [itineraryEndDate, setItineraryEndDate] = useState("");

  // Additional deal config
  const [numberOfNights, setNumberOfNights] = useState("");
  const [causePercentage, setCausePercentage] = useState(1.05);
  const [causeAmount, setCauseAmount] = useState(0);
  const [publishedRate, setPublishedRate] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [showDiscount, setShowDiscount] = useState(true);
  const [dealsType, setDealsType] = useState(["featured"]);
  const [displayOrder, setDisplayOrder] = useState("");
  const [imageValidationError, setImageValidationError] = useState("");
  const [imageValidationSuccess, setImageValidationSuccess] = useState("");

  // Fetched hotel data
  const [hotelDetails, setHotelDetails] = useState(null);

  // Hero image selection
  const [selectedHeroImage, setSelectedHeroImage] = useState("");
  const [heroPreviewIndex, setHeroPreviewIndex] = useState(0);
  const [heroImagePreview, setHeroImagePreview] = useState("");

  useEffect(() => {
    let url;
    if (selectedHeroImage instanceof File) {
      url = URL.createObjectURL(selectedHeroImage);
      setHeroImagePreview(url);
    } else {
      setHeroImagePreview(selectedHeroImage || "");
    }
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [selectedHeroImage]);

  const handleImageChange = (file) => {
    if (!file) return;
    setImageValidationError("");
    setImageValidationSuccess("");

    // 1. Format validation
    const allowedFormats = ["image/jpeg", "image/png", "image/webp", "image/jpg", "image/gif"];
    if (!allowedFormats.includes(file.type)) {
      const errorMsg = "Invalid format. Please upload JPEG, PNG, WEBP, or GIF.";
      setImageValidationError(errorMsg);
      toast.error(errorMsg);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // 2. Size validation (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      const errorMsg = "File size exceeds 10MB limit.";
      setImageValidationError(errorMsg);
      toast.error(errorMsg);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setImageValidationSuccess(`Validated! Format: ${file.type.split("/")[1].toUpperCase()}, Size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
    setSelectedHeroImage(file);
  };

  const handleThumbnailSelect = (url) => {
    setSelectedHeroImage(url);
    setImageValidationError("");
    setImageValidationSuccess("Zentum Verified Hotel Image (High Quality)");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Room images (from selected room's data or hotel images)
  const [roomImages, setRoomImages] = useState([]);

  // Room/Rate selections
  const [selectedRoomTypeId, setSelectedRoomTypeId] = useState("");
  const [selectedRatePlanId, setSelectedRatePlanId] = useState("");
  const [selectedRefundability, setSelectedRefundability] = useState("Non-refundable");
  // Zentrum's standardized room id + wholesaler id for the exact rate selected —
  // roomTypeId (Zentrum's recommendation groupId) alone isn't stable across live
  // searches, so these pin live pricing to the same room/supplier chosen here.
  const [selectedStdRoomId, setSelectedStdRoomId] = useState("");
  const [selectedProviderId, setSelectedProviderId] = useState("");

  // Booking details (auto-populated on selection)
  const [roomTypeName, setRoomTypeName] = useState("");
  const [ratePlanName, setRatePlanName] = useState("");
  const [selectedRateOptionId, setSelectedRateOptionId] = useState("");
  const [selectedSupplierName, setSelectedSupplierName] = useState("");
  const [refundability, setRefundability] = useState("");
  const [cancellationPolicy, setCancellationPolicy] = useState("");
  const [baseRate, setBaseRate] = useState("");
  const [taxesAndFees, setTaxesAndFees] = useState("");
  const [totalBookingAmount, setTotalBookingAmount] = useState("");

  const showError = (err) =>
    toast.error(err?.response?.data?.message || err?.response?.data?.error || "Something went wrong");

  const today = getTodayDate();
  const minStartDate = isEdit ? (savedStartDate || today) : today;
  const numOfAdults = Math.max(1, Number(numOfAdultsInput || 1));
  const numOfChildren = Math.max(0, Number(numOfChildrenInput || 0));
  const canFetch = hotelId.trim() && dealStartDate && dealEndDate && dealStartDate >= minStartDate && dealEndDate > dealStartDate;

  const commitStartDate = (nextStart) => {
    setDealStartDate(nextStart);
    setDealEndDate((prev) => (prev && prev <= nextStart ? "" : prev));
    setHotelDetails(null);
    setTimeout(() => endDateInputRef.current?.open(nextStart), 0);
  };

  const commitEndDate = (nextEnd) => {
    setDealEndDate(nextEnd);
    setHotelDetails(null);
  };

  // ── Auto-calculate nights ─────────────────────────────────────────────────
  useEffect(() => {
    if (dealStartDate && dealEndDate) {
      const nights = Math.round((new Date(dealEndDate) - new Date(dealStartDate)) / 86400000);
      setNumberOfNights(nights > 0 ? nights : "");
    } else {
      setNumberOfNights("");
    }
  }, [dealStartDate, dealEndDate]);

  // ── Auto-calculate Cause Amount ────────────────────────────────────────────
  useEffect(() => {
    const total = parseFloat(totalBookingAmount) || 0;
    const pct = parseFloat(causePercentage) || 0;
    const amt = (total * pct) / 100;
    setCauseAmount(parseFloat(amt.toFixed(2)));
  }, [totalBookingAmount, causePercentage]);

  // ── Auto-calculate Discount ────────────────────────────────────────────────
  useEffect(() => {
    const pub = parseFloat(publishedRate) || 0;
    const sevenly = parseFloat(totalBookingAmount) || 0;
    if (pub > sevenly && pub > 0) {
      const diff = pub - sevenly;
      const pct = (diff / pub) * 100;
      setDiscountAmount(parseFloat(diff.toFixed(2)));
      setDiscountPercentage(parseFloat(pct.toFixed(2)));
    } else {
      setDiscountAmount(0);
      setDiscountPercentage(0);
    }
  }, [publishedRate, totalBookingAmount]);

  const resetSelections = () => {
    setSelectedHeroImage("");
    setHeroPreviewIndex(0);
    setImageValidationError("");
    setImageValidationSuccess("");
    setRoomImages([]);
    setSelectedRoomTypeId(""); setSelectedRatePlanId("");
    setSelectedRefundability("Non-refundable");
    setSelectedRateOptionId("");
    setSelectedSupplierName("");
    setSelectedStdRoomId(""); setSelectedProviderId("");
    setRoomTypeName(""); setRatePlanName("");
    setRefundability(""); setCancellationPolicy(""); setBaseRate(""); setTaxesAndFees(""); setTotalBookingAmount("");
    setCausePercentage(1.05);
    setCauseAmount(0);
    setPublishedRate(0);
    setDiscountAmount(0);
    setDiscountPercentage(0);
    setShowDiscount(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // ── Fetch Hotel Details ───────────────────────────────────────────────────
  const fetchHotelDetails = async (targetId, checkIn, checkOut, isInitialEditLoad = false, existingHeroImage = undefined, overrideAdults = undefined, overrideChildren = undefined) => {
    const idToFetch = (targetId || hotelId || "").trim();
    const ci = checkIn || dealStartDate;
    const co = checkOut || dealEndDate;
    if (!idToFetch) { toast.error("Please enter a hotel ID"); return; }
    if (!ci || !co) { toast.error("Please select start and end dates first"); return; }
    const effectiveAdults = overrideAdults !== undefined ? overrideAdults : numOfAdults;
    const effectiveChildren = overrideChildren !== undefined ? overrideChildren : numOfChildren;

    if (!isInitialEditLoad) {
      if (ci < minStartDate) {
        toast.error(isEdit ? "Start Date cannot be before the saved Start Date" : "Start Date cannot be in the past");
        return;
      }
      if (co <= ci) {
        toast.error("End Date must be after Start Date");
        return;
      }
    }

    setFetchingHotel(true);
    setHotelDetails(null);
    if (!isInitialEditLoad) {
      resetSelections();
    }

    try {
      const res = await Axios.get(`${BACKEND_URL}hotel/${idToFetch}/details`, {
        params: { checkIn: ci, checkOut: co, numOfAdults: effectiveAdults, numOfChildren: effectiveChildren },
      });
      if (res?.data?.success) {
        const data = res.data.data;
        setHotelDetails(data);
        setNumOfAdultsInput(String(Number(data?.numOfAdults) || effectiveAdults));
        setNumOfChildrenInput(String(Number(data?.numOfChildren) || effectiveChildren));
        // Auto-select first hero image if none is currently selected/loaded (preserve a saved/uploaded image on edit)
        if (data.heroImages?.length > 0 && !existingHeroImage && !selectedHeroImage) {
          setSelectedHeroImage(data.heroImages[0]);
        }
        // Set room images from API hotel images if none are currently set (preserve saved images on edit)
        if (data.roomImages?.length > 0 && !isInitialEditLoad && roomImages.length === 0) {
          setRoomImages(data.roomImages.map((r) => r.url || r));
        }
        toast.success("Hotel details loaded");
      } else {
        toast.error(res?.data?.message || "Failed to load hotel details");
      }
    } catch (err) { showError(err); }
    finally { setFetchingHotel(false); }
  };

  // ── Load existing deal (edit mode) ───────────────────────────────────────
  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    Axios.get(`${BACKEND_URL}featured-deal/${id}`)
      .then((res) => {
        const data = res?.data?.data || {};
        const ci = data.dealStartDate ? data.dealStartDate.split("T")[0] : "";
        const co = data.dealEndDate ? data.dealEndDate.split("T")[0] : "";
        setSavedStartDate(ci || "");
        setHotelId(data.hotelId || "");
        setNumOfAdultsInput(data.numOfAdults !== undefined ? String(data.numOfAdults) : "2");
        setNumOfChildrenInput(data.numOfChildren !== undefined ? String(data.numOfChildren) : "0");
        setNumberOfNights(data.numberOfNights || "");
        setCausePercentage(data.causePercentage !== undefined ? data.causePercentage : 1.05);
        setCauseAmount(data.causeAmount !== undefined ? data.causeAmount : (data.causePrice !== undefined ? data.causePrice : 0));
        setPublishedRate(data.publishedRate !== undefined ? data.publishedRate : 0);
        setDiscountAmount(data.discountAmount !== undefined ? data.discountAmount : 0);
        setDiscountPercentage(data.discountPercentage !== undefined ? data.discountPercentage : 0);
        setShowDiscount(data.showDiscount !== undefined ? data.showDiscount : true);
        setDealsType(Array.isArray(data.dealsType) ? data.dealsType : (data.dealsType ? [data.dealsType] : ["featured"]));
        setDisplayOrder(data.displayOrder !== undefined ? String(data.displayOrder) : "");
        const iStart = data.itineraryStartDate ? data.itineraryStartDate.split("T")[0] : "";
        const iEnd = data.itineraryEndDate ? data.itineraryEndDate.split("T")[0] : "";
        setItineraryStartDate(iStart);
        setItineraryEndDate(iEnd);
        setDealStartDate(ci); setDealEndDate(co);
        const heroImg = data.heroImage || "";
        setSelectedHeroImage(heroImg);
        setSelectedRoomTypeId(data.roomTypeId || "");
        setSelectedRatePlanId(data.ratePlanId || "");
        setRoomTypeName(data.roomTypeName || "");
        setRatePlanName(data.ratePlanName || "");
        setSelectedStdRoomId(data.stdRoomId || "");
        setSelectedProviderId(data.providerId || "");
        const existingRefundability = String(data.refundability || "").toLowerCase().includes("non")
          ? "Non-refundable"
          : String(data.refundability || "").toLowerCase().includes("refund")
            ? "Refundable"
            : "Non-refundable";
        setRefundability(existingRefundability);
        setSelectedRefundability(existingRefundability);
        setCancellationPolicy(data.cancellationPolicy || "");
        setBaseRate(data.baseRate || "");
        setTaxesAndFees(data.taxesAndFees || "");
        setTotalBookingAmount(data.totalBookingAmount || "");
        setRoomImages(Array.isArray(data.roomImages) ? data.roomImages : []);
        const savedAdults = Math.max(1, Number(data.numOfAdults) || 2);
        const savedChildren = Math.max(0, Number(data.numOfChildren) || 0);
        if (data.hotelId && ci && co) {
          fetchHotelDetails(data.hotelId, ci, co, true, heroImg, savedAdults, savedChildren);
        }
      })
      .catch(showError)
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [BACKEND_URL, id, isEdit]);

  // ── Rate matching helpers ─────────────────────────────────────────────────
  const allRates = hotelDetails?.roomsAndRates || [];
  const ratesForRoom = (groupId) => allRates.filter((r) => r.groupId === groupId);
  const normalizeRefundability = (rate = {}) => {
    const raw = String(rate?.refundability || "").toLowerCase();
    if (raw.includes("refund")) {
      return raw.includes("non") ? "Non-refundable" : "Refundable";
    }
    if (rate?.refundable === true) return "Refundable";
    return "Non-refundable";
  };

const groupedRatePlansForRoom = () => {
  const candidates = selectedRoomTypeId ? ratesForRoom(selectedRoomTypeId) : allRates;
  if (candidates.length === 0) return [];
  const grouped = new Map();
  candidates.forEach((r) => {
    const planId = r.boardBasisType || r.boardBasis?.type;
    if (!planId) return;
    const refundLabel = normalizeRefundability(r);
    const supplierName = getRateSupplierName(r);
    const rateId = r.id || r.recommendationId || "";
    const total = parseFloat(r.totalBookingAmount || 0);
    if (!grouped.has(planId)) {
      grouped.set(planId, {
        planId,
        name: r.boardBasisName || r.boardBasis?.description || "Room Only",
        cheapest: new Map(), // key: refundLabel → cheapest option
      });
    }
    const current = grouped.get(planId);
    const existing = current.cheapest.get(refundLabel);
    if (!existing || total < parseFloat(existing.totalBookingAmount || 0)) {
      const optionId = buildRateOptionId({ planId, refundLabel, supplierName, rateId });
      current.cheapest.set(refundLabel, {
        optionId,
        refundLabel,
        supplierName,
        rateId,
        totalBookingAmount: r.totalBookingAmount || "0.00",
        baseRate: r.baseRate || "0.00",
        taxesAndFees: r.taxesAndFees || "0.00",
      });
    }
  });
  return Array.from(grouped.values()).map((plan) => ({
    planId: plan.planId,
    name: plan.name,
    options: Array.from(plan.cheapest.values()),
  }));
};

  // ── Room Type Selection ───────────────────────────────────────────────────
  const applyRateMatch = (candidates, selectedPlan) => {
    const match =
      (selectedPlan?.rateId && candidates.find((r) => r.id === selectedPlan.rateId)) ||
      candidates.find((r) => {
        const samePlan = (r.boardBasisType || r.boardBasis?.type) === selectedPlan?.planId;
        const sameRefund = normalizeRefundability(r) === selectedPlan?.refundLabel;
        const sameSupplier = selectedPlan?.supplierName ? getRateSupplierName(r) === selectedPlan.supplierName : true;
        return samePlan && sameRefund && sameSupplier;
      }) ||
      candidates.find((r) => (r.boardBasisType || r.boardBasis?.type) === selectedPlan?.planId) ||
      candidates[0];

    if (match) {
      const supplierName = getRateSupplierName(match);
      const optionId = selectedPlan?.optionId || buildRateOptionId({
        planId: match.boardBasisType || match.boardBasis?.type,
        refundLabel: normalizeRefundability(match),
        supplierName,
        rateId: match.id || match.recommendationId,
      });

      setRefundability(normalizeRefundability(match));
      setCancellationPolicy(match.cancellationPolicy || "Cancellation policy subject to terms.");
      setBaseRate(match.baseRate || "0.00");
      setTaxesAndFees(match.taxesAndFees || "0.00");
      setTotalBookingAmount(match.totalBookingAmount || "0.00");
      setSelectedRefundability(normalizeRefundability(match));
      setSelectedSupplierName(supplierName);
      setSelectedRateOptionId(optionId);
      setSelectedStdRoomId(match.stdRoomId || "");
      setSelectedProviderId(match.providerId || "");
    }
  };

  const handleRoomSelect = (rt, presetRatePlanId = null) => {
    setSelectedRoomTypeId(rt.id);
    setRoomTypeName(rt.name);
    
    const candidates = ratesForRoom(rt.id);
    if (candidates.length > 0) {
      const seen = new Set();
      const plans = candidates
        .map((r) => {
          const planId = r.boardBasisType || r.boardBasis?.type;
          const refundLabel = normalizeRefundability(r);
          return {
            id: `${planId}__${refundLabel.toLowerCase().replace(/[^a-z]/g, "")}`,
            planId,
            refundLabel,
            name: r.boardBasisName || r.boardBasis?.description || "Room Only",
            supplierName: getRateSupplierName(r),
            rateId: r.id || r.recommendationId || "",
            optionId: buildRateOptionId({
              planId,
              refundLabel,
              supplierName: getRateSupplierName(r),
              rateId: r.id || r.recommendationId,
            }),
          };
        })
        .filter((rp) => rp.planId && !seen.has(rp.id) && seen.add(rp.id));
      
      if (plans.length > 0) {
        const defaultNonRefundable = plans.find((p) => p.refundLabel === "Non-refundable");
        const targetPlan = presetRatePlanId 
          ? (plans.find((p) => p.planId === presetRatePlanId) || defaultNonRefundable || plans[0])
          : (defaultNonRefundable || plans[0]);
        
        setSelectedRatePlanId(targetPlan.planId || "");
        setSelectedRefundability(targetPlan.refundLabel || "Non-refundable");
        setRatePlanName(targetPlan.name);
        setSelectedRateOptionId(targetPlan.optionId || "");
        applyRateMatch(candidates, targetPlan);
      } else {
        setSelectedRatePlanId(""); setRatePlanName("");
        setSelectedRateOptionId(""); setSelectedSupplierName("");
        setSelectedStdRoomId(""); setSelectedProviderId("");
        setRefundability(""); setCancellationPolicy(""); setBaseRate(""); setTaxesAndFees(""); setTotalBookingAmount("");
      }
    } else {
      setSelectedRatePlanId(""); setRatePlanName("");
      setSelectedRateOptionId(""); setSelectedSupplierName("");
      setSelectedStdRoomId(""); setSelectedProviderId("");
      setRefundability(""); setCancellationPolicy(""); setBaseRate(""); setTaxesAndFees(""); setTotalBookingAmount("");
    }

    // Show room images for this group
    if (hotelDetails?.roomImages?.length > 0) {
      const roomCodesInGroup = new Set();
      candidates.forEach((r) => { (r.occupancies || []).forEach((occ) => { if (occ.roomId) roomCodesInGroup.add(occ.roomId); }); });
      const filtered = hotelDetails.roomImages
        .filter((img) => !img.roomCodes?.length || img.roomCodes.some((rc) => roomCodesInGroup.has(rc)))
        .map((img) => img.url || img);
      setRoomImages(filtered.length > 0 ? filtered : hotelDetails.roomImages.map((img) => img.url || img));
    }
  };

  // ── Rate Plan Selection → auto-populate pricing ───────────────────────────
  const handleRateSelect = (rp) => {
    setSelectedRatePlanId(rp.planId);
    setSelectedRefundability(rp.refundLabel);
    setRatePlanName(rp.name);
    setSelectedRateOptionId(rp.optionId || "");
    setSelectedSupplierName(rp.supplierName || "");
    const candidates = selectedRoomTypeId ? ratesForRoom(selectedRoomTypeId) : allRates;
    applyRateMatch(candidates, rp);
  };

  // ── Auto-select first room + rate when hotel loads ────────────────────────
  useEffect(() => {
    if (!hotelDetails) return;
    const rates = hotelDetails.roomsAndRates || [];
    if (rates.length === 0) return;

    // In edit mode, if we already have selectedRoomTypeId and selectedRatePlanId set,
    // let's try to locate the corresponding rate from the fetched roomsAndRates to ensure
    // we keep the active selections and populate the image gallery properly.
    if (isEdit && selectedRoomTypeId && selectedRatePlanId) {
      // Only stdRoomId+providerId TOGETHER is a stable, trustworthy pair —
      // verified empirically that the same numeric stdRoomId can be reused by
      // a different provider for a completely unrelated physical room, so
      // matching on stdRoomId alone (without pinning the provider too) can
      // silently select the wrong room. roomTypeId (Zentrum's recommendation
      // groupId) is even less reliable — it can surface a different physical
      // room on every fetch — so it's never used to narrow the match here.
      // Fall back to a room-name match (a stable descriptive label) when the
      // exact stdRoomId+providerId pair isn't being offered right now.
      const matchedRate = (selectedStdRoomId && selectedProviderId)
        ? rates.find((r) => r.stdRoomId === selectedStdRoomId && r.providerId === selectedProviderId)
        : null;

      const activeRoomType = matchedRate
        ? { id: matchedRate.groupId, name: matchedRate.name || roomTypeName || "Standard Room" }
        : hotelDetails.roomTypes?.find((rt) => roomTypeName && rt.name?.toLowerCase() === roomTypeName.toLowerCase());

      if (activeRoomType) {
        handleRoomSelect(activeRoomType, selectedRatePlanId);
        return;
      }
      // No reliable match — the saved room/rate isn't being offered under
      // this or any similarly-named listing right now. Fall through to the
      // same "pick the first available room" default a brand-new deal gets,
      // rather than risk a coincidental (and possibly wrong) match on the
      // unstable groupId.
    }

    // Pick first room type (index 0) from roomTypes or rates
    const roomTypes = hotelDetails.roomTypes || [];
    if (roomTypes.length > 0) {
      handleRoomSelect(roomTypes[0]);
    } else {
      const firstRate = rates[0];
      const firstRoomType = { id: firstRate.groupId, name: firstRate.name || "Standard Room" };
      handleRoomSelect(firstRoomType);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelDetails]);

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!hotelId || !dealStartDate || !dealEndDate || !selectedRoomTypeId || !selectedRatePlanId) {
      toast.error("Please fill in all required fields (Hotel ID, Dates, Room type, Rate plan)");
      return;
    }
    if (!totalBookingAmount) {
      toast.error("Please select a Room Type and Rate Plan to load pricing details.");
      return;
    }

    if (dealStartDate < minStartDate) {
      toast.error(isEdit ? "Start Date cannot be before the saved Start Date" : "Start Date cannot be in the past");
      return;
    }
    if (dealEndDate <= dealStartDate) {
      toast.error("End Date must be after Start Date");
      return;
    }
    const pctValue = parseFloat(causePercentage);
    if (!Number.isFinite(pctValue) || pctValue < 0 || pctValue > 100) {
      toast.error("Cause % must be a number between 0 and 100");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("hotelId", hotelId);
    formData.append("numOfAdults", String(Number(numOfAdults)));
    formData.append("numOfChildren", String(Number(numOfChildren)));
    
    if (selectedHeroImage instanceof File) {
      formData.append("customHeroImage", selectedHeroImage);
    } else {
      formData.append("heroImage", selectedHeroImage || "");
    }
    
    formData.append("destination", hotelDetails?.destination || "");
    formData.append("numberOfNights", numberOfNights ? String(parseInt(numberOfNights)) : "0");
    formData.append("causePrice", String(causeAmount));
    formData.append("causePercentage", String(causePercentage));
    formData.append("causeAmount", String(causeAmount));
    formData.append("publishedRate", String(publishedRate));
    formData.append("discountAmount", String(discountAmount));
    formData.append("discountPercentage", String(discountPercentage));
    formData.append("showDiscount", String(showDiscount));
    formData.append("dealsType", JSON.stringify(dealsType.length > 0 ? dealsType : ["featured"]));
    formData.append("displayOrder", String(Number(displayOrder) || 0));
    formData.append("dealStartDate", dealStartDate);
    formData.append("dealEndDate", dealEndDate);
    formData.append("itineraryStartDate", itineraryStartDate || "");
    formData.append("itineraryEndDate", itineraryEndDate || "");
    formData.append("roomTypeId", selectedRoomTypeId);
    formData.append("roomTypeName", roomTypeName);
    formData.append("stdRoomId", selectedStdRoomId);
    formData.append("providerId", selectedProviderId);
    formData.append("ratePlanId", selectedRatePlanId);
    formData.append("ratePlanName", ratePlanName);
    formData.append("refundability", refundability);
    formData.append("cancellationPolicy", cancellationPolicy);
    formData.append("baseRate", String(Number(baseRate)));
    formData.append("taxesAndFees", String(Number(taxesAndFees)));
    formData.append("totalBookingAmount", String(Number(totalBookingAmount)));
    formData.append("roomImages", JSON.stringify(roomImages));

    try {
      if (isEdit) {
        formData.append("id", id);
        const res = await Axios.post(`${BACKEND_URL}update-featured-deal-details`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        toast.success(res?.data?.message || "Updated successfully");
      } else {
        const res = await Axios.post(`${BACKEND_URL}create-featured-deal`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        toast.success(res?.data?.message || "Created successfully");
      }
      navigate("/feature-deals");
    } catch (err) { showError(err); }
    finally { setLoading(false); }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────────
  const heroImages = hotelDetails?.heroImages || [];

  return (
    <Box className="container-fluid" sx={formSx}>
      <Helmet><title>{isEdit ? "Edit Feature Deal" : "Add Feature Deal"}</title></Helmet>

      <div className="d-flex justify-content-between align-center mb-4">
        <h2 className="form-title" style={{ fontWeight: 600 }}>{isEdit ? "Edit Feature Deal" : "Add Feature Deal"}</h2>
        <Button startIcon={<ArrowBack />} onClick={() => navigate("/feature-deals")}>Back</Button>
      </div>

      {loading && !hotelDetails ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={3}>

          {/* ── Step 1 ────────────────────────────────────────────────────── */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Step 1: Enter Hotel ID &amp; Travel Dates
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Hotel ID *" value={hotelId}
                      onChange={(e) => { setHotelId(e.target.value); setHotelDetails(null); resetSelections(); }}
                      disabled={isEdit} placeholder="e.g. 39773507" />
                  </Grid>
                  <Grid item xs={6} md={1}>
                    <TextField fullWidth type="number" label="Adults *" value={numOfAdultsInput}
                      inputProps={{ min: 1, step: 1 }}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (/^\d*$/.test(val)) {
                          setNumOfAdultsInput(val);
                          setHotelDetails(null);
                        }
                      }}
                      onBlur={() => setNumOfAdultsInput(String(Math.max(1, Number(numOfAdultsInput || 1))))}
                    />
                  </Grid>
                  <Grid item xs={6} md={1}>
                    <TextField fullWidth type="number" label="Children *" value={numOfChildrenInput}
                      inputProps={{ min: 0, step: 1 }}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (/^\d*$/.test(val)) {
                          setNumOfChildrenInput(val);
                          setHotelDetails(null);
                        }
                      }}
                      onBlur={() => setNumOfChildrenInput(String(Math.max(0, Number(numOfChildrenInput || 0))))}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <DateInputField
                      label="Start Date"
                      required
                      value={dealStartDate}
                      min={minStartDate}
                      onCommit={commitStartDate}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <DateInputField
                      ref={endDateInputRef}
                      label="End Date"
                      required
                      value={dealEndDate}
                      min={getMinEndDate(dealStartDate || minStartDate)}
                      onCommit={commitEndDate}
                      rangeStart={dealStartDate}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button variant="contained" startIcon={fetchingHotel ? null : <Search />}
                      onClick={() => fetchHotelDetails()} disabled={fetchingHotel || !canFetch} fullWidth sx={{ height: 56 }}>
                      {fetchingHotel ? <><CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />Loading…</> : "Fetch Details"}
                    </Button>
                  </Grid>
                </Grid>
                {numberOfNights && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    🌙 {numberOfNights} night{numberOfNights > 1 ? "s" : ""} selected
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>


          {hotelDetails && (
            <>
              {/* ── Hotel Info ───────────────────────────────────────────── */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Hotel Details</Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <TextField fullWidth label="Hotel Name" value={hotelDetails.hotelName || ""} disabled />
                      <TextField fullWidth label="Destination" value={hotelDetails.destination || ""} disabled />
                    </Box>

                    {/* ── Hero Image Selection ─────────────────────────── */}
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        <PhotoCamera sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }} />
                        Select Hero Image (click to select)
                      </Typography>

                      {/* Big preview of selected image */}
                      {heroImagePreview ? (
                        <Box sx={{ width: "100%", height: 200, borderRadius: 2, overflow: "hidden", mb: 1, position: "relative", border: "2px solid #1976d2" }}>
                          <img src={heroImagePreview} alt="Selected hero" style={{ width: "100%", height: "100%", objectFit: "contain", backgroundColor: "#f7f7f7" }} />
                          <Box sx={{ position: "absolute", bottom: 8, left: 8, bgcolor: "rgba(25,118,210,0.85)", color: "#fff", fontSize: 11, px: 1, py: 0.3, borderRadius: 1 }}>
                            ✓ Selected
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: "italic" }}>
                          No hero image selected yet.
                        </Typography>
                      )}

                      {/* Thumbnail grid */}
                      {heroImages.length > 0 && (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                          {heroImages.map((url, idx) => (
                            <Box key={idx} onClick={() => handleThumbnailSelect(url)} sx={{
                              width: 80, height: 60, borderRadius: 1, overflow: "hidden", cursor: "pointer",
                              border: selectedHeroImage === url ? "2px solid #1976d2" : "2px solid transparent",
                              opacity: selectedHeroImage === url ? 1 : 0.7, transition: "all 0.2s",
                              "&:hover": { opacity: 1 },
                            }}>
                              <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            </Box>
                          ))}
                        </Box>
                      )}

                      {/* Custom upload input */}
                      <TextField
                        type="file"
                        fullWidth
                        inputRef={fileInputRef}
                        inputProps={{ accept: "image/*" }}
                        onChange={(e) => {
                          const file = e.target.files?.[0] || null;
                          handleImageChange(file);
                        }}
                        helperText={selectedHeroImage instanceof File ? `Selected custom file: ${selectedHeroImage.name}` : "Or upload a custom hero image (max 10MB, any image format)"}
                        sx={{ mt: 2 }}
                      />

                      {imageValidationError && (
                        <Typography color="error" variant="caption" sx={{ mt: 1, display: "block", fontWeight: 500 }}>
                          ⚠️ {imageValidationError}
                        </Typography>
                      )}
                      {imageValidationSuccess && (
                        <Typography color="success.main" variant="caption" sx={{ mt: 1, display: "block", fontWeight: 500 }}>
                          ✓ {imageValidationSuccess}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* ── Deal Config ───────────────────────────────────────────── */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Deal Configuration</Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField fullWidth label="Number of Nights" type="number" value={numberOfNights} disabled helperText="Auto-calculated from dates" />
                        <TextField fullWidth label="Cause %" type="number" value={causePercentage}
                          onChange={(e) => setCausePercentage(e.target.value)}
                          helperText="Percentage of total donated to the cause" inputProps={{ step: "0.01", min: 0, max: 100 }} />
                        <TextField fullWidth label="Cause Dollar Amount ($)" type="number" value={causeAmount} disabled
                          helperText="Auto-calculated from Cause %" InputProps={{ style: { fontWeight: "bold" } }} />
                      </Box>
                      <TextField
                        fullWidth
                        label="Display Order"
                        type="number"
                        value={displayOrder}
                        onChange={(e) => setDisplayOrder(e.target.value)}
                        helperText="Lower number appears first (0 = default)"
                        inputProps={{ min: 0, step: 1 }}
                      />
                      <Box>
                        <InputLabel sx={{ mb: 1, fontWeight: 600 }}>Deals Type</InputLabel>
                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                          {["featured", "trending", "last-minute"].map((type) => (
                            <FormControlLabel key={type}
                              control={<Checkbox checked={dealsType.includes(type)}
                                onChange={(e) => {
                                  if (e.target.checked) setDealsType([...dealsType, type]);
                                  else setDealsType(dealsType.filter((t) => t !== type));
                                }} />}
                              label={type.charAt(0).toUpperCase() + type.slice(1).replace("-", " ")} />
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* ── Room Images ───────────────────────────────────────────── */}
              {roomImages.length > 0 && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Room Images
                        {selectedRoomTypeId && <Chip label={roomTypeName} size="small" sx={{ ml: 1 }} />}
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        {roomImages.map((url, idx) => (
                          <Box key={idx} sx={{ width: 200, height: 140, borderRadius: 2, overflow: "hidden" }}>
                            <img src={url} alt={`Room ${idx + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* ── Room Types ────────────────────────────────────────────── */}
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
                      Available Room Types
                      {selectedRoomTypeId && <Chip label="Selected" size="small" color="primary" sx={{ ml: 1 }} />}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Select a room type to filter rate plans</Typography>
                    {(hotelDetails.roomTypes || []).length === 0 ? (
                      <Typography variant="body2" color="warning.main">No room types found for the selected dates.</Typography>
                    ) : (
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 2 }}>
                        {hotelDetails.roomTypes.map((rt, idx) => (
                          <Box key={rt.id} onClick={() => handleRoomSelect(rt)} sx={{
                            p: 2.5, borderRadius: 2, cursor: "pointer", transition: "all 0.2s ease",
                            border: selectedRoomTypeId === rt.id ? "2px solid #1976d2" : "1px solid #e0e0e0",
                            backgroundColor: selectedRoomTypeId === rt.id ? "#ffffff" : "#fafafa",
                            color: selectedRoomTypeId === rt.id ? "#1976d2" : "inherit",
                            "&:hover": { borderColor: "#1976d2", backgroundColor: "#ffffff" },
                          }}>
                            {selectedRoomTypeId === rt.id && <CheckCircle sx={{ color: "#1976d2", fontSize: 18, mb: 0.5, display: "block" }} />}
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{`${idx + 1}. ${rt.name}`}</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* ── Rate Plans ────────────────────────────────────────────── */}
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
                      Available Rate Plans
                      {selectedRatePlanId && <Chip label="Selected" size="small" color="primary" sx={{ ml: 1 }} />}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {selectedRoomTypeId ? "Showing rate plans for selected room type" : "Select a room type first to filter rate plans"}
                    </Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))", gap: 2 }}>
                      {groupedRatePlansForRoom().map((plan) => (
                        <Box key={plan.planId} sx={{
                          p: 2.5, borderRadius: 2, border: "1px solid #e0e0e0", backgroundColor: "#fafafa",
                        }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{plan.name}</Typography>
                          <RadioGroup
                            value={selectedRatePlanId === plan.planId ? selectedRateOptionId : ""}
                            onChange={(e) => {
                              const option = plan.options.find((opt) => opt.optionId === e.target.value);
                              if (option) {
                                handleRateSelect({
                                  planId: plan.planId,
                                  name: plan.name,
                                  refundLabel: option.refundLabel,
                                  supplierName: option.supplierName,
                                  rateId: option.rateId,
                                  optionId: option.optionId,
                                });
                              }
                            }}
                          >
                            {plan.options.map((opt) => (
                              <Box key={`${plan.planId}-${opt.refundLabel}`} sx={{ border: "1px solid #e5e5e5", borderRadius: 1.5, p: 1.5, mb: 1.2, backgroundColor: "#fff" }}>
                                <FormControlLabel
                                  value={opt.optionId}
                                  control={<Radio size="small" />}
                                  label={opt.refundLabel}
                                  sx={{ m: 0 }}
                                />
                                <Typography variant="body2" color="text.secondary">Supplier: {opt.supplierName || "-"}</Typography>
                                <Typography variant="body2" color="text.secondary">Total: ${parseFloat(opt.totalBookingAmount || 0).toFixed(2)}</Typography>
                                <Typography variant="caption" color="text.secondary">Base: ${parseFloat(opt.baseRate || 0).toFixed(2)} | Taxes: ${parseFloat(opt.taxesAndFees || 0).toFixed(2)}</Typography>
                              </Box>
                            ))}
                          </RadioGroup>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* ── Booking Details & Discount Display (auto-populated) ──────────────────────── */}
              {totalBookingAmount && (
                <>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ borderColor: "#1976d2", borderWidth: 2, height: "100%" }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Booking Details</Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField fullWidth label="Room Name" value={roomTypeName} disabled />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField fullWidth label="Rate Plan" value={ratePlanName} disabled />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField fullWidth label="Supplier" value={selectedSupplierName} disabled />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField fullWidth label="Adults" value={numOfAdults} disabled />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField fullWidth label="Children" value={numOfChildren} disabled />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField fullWidth label="Refundability" value={refundability} disabled />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField fullWidth label="Base Rate ($)" value={baseRate} disabled />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField fullWidth label="Taxes & Fees ($)" value={taxesAndFees} disabled />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField fullWidth label="Total Booking Amount ($)" value={totalBookingAmount} disabled
                              InputProps={{ style: { fontWeight: "bold", color: "#2e7d32" } }} />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField fullWidth label="Cancellation Policy" value={cancellationPolicy} disabled multiline maxRows={2} />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: "100%", borderColor: "#2e7d32", borderWidth: 2 }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: "#2e7d32" }}>
                          Discount & Margin Control
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Published Rate ($) *"
                              type="number"
                              value={publishedRate || ""}
                              onChange={(e) => {
                                const clean = e.target.value.replace(/^0+(?=\d)/, '');
                                e.target.value = clean;
                                setPublishedRate(parseFloat(clean) || 0);
                              }}
                              placeholder="Enter compared public rate (e.g. 1000)"
                              helperText="Enter the official published rate for discount comparison."
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Sevenly Total Rate ($)"
                              value={totalBookingAmount}
                              disabled
                              helperText="Auto-populated from selected room rate"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Discount Amount ($)"
                              value={discountAmount}
                              disabled
                              helperText="Auto-calculated"
                              InputProps={{ style: { fontWeight: "bold" } }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Discount Percentage (%)"
                              value={discountPercentage > 0 ? `${discountPercentage}%` : "0%"}
                              disabled
                              helperText="Auto-calculated"
                              InputProps={{ style: { fontWeight: "bold", color: discountPercentage > 0 ? "#2e7d32" : "inherit" } }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={showDiscount}
                                  onChange={(e) => setShowDiscount(e.target.checked)}
                                  color="success"
                                />
                              }
                              label="Display Discount Badge on Homepage UX"
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </>
              )}

              {/* ── Save/Cancel ───────────────────────────────────────────── */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button variant="contained" size="large" onClick={handleSubmit} disabled={loading}>
                    {loading ? <CircularProgress size={22} color="inherit" sx={{ mr: 1 }} /> : null}
                    {isEdit ? "Update Featured Deal" : "Create Featured Deal"}
                  </Button>
                  <Button variant="outlined" size="large" onClick={() => navigate("/feature-deals")}>Cancel</Button>
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default FeatureDealsForm;
